import React, { useEffect, useState } from 'react';
import { adminCarFeaturesService } from '../../services/adminService';

const CarFeaturesForm = ({ carId }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    category: 'Safety', 
    name: '', 
    available: true 
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (carId) {
      loadFeatures();
    }
  }, [carId]);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const res = await adminCarFeaturesService.getFeatures(carId);
      setFeatures(res.features || []);
    } catch (error) {
      console.error('Error loading features:', error);
      alert('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Feature name is required');
      return;
    }

    try {
      if (editingId) {
        await adminCarFeaturesService.updateFeature(carId, editingId, form);
        alert('Feature updated successfully!');
      } else {
        await adminCarFeaturesService.createFeature(carId, form);
        alert('Feature added successfully!');
      }
      setForm({ category: 'Safety', name: '', available: true });
      setEditingId(null);
      loadFeatures();
    } catch (error) {
      alert('Failed to save feature: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (feature) => {
    setForm({
      category: feature.category,
      name: feature.name,
      available: feature.available
    });
    setEditingId(feature.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;
    
    try {
      await adminCarFeaturesService.deleteFeature(carId, id);
      alert('Feature deleted successfully!');
      loadFeatures();
    } catch (error) {
      alert('Failed to delete feature: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancel = () => {
    setForm({ category: 'Safety', name: '', available: true });
    setEditingId(null);
  };

  const categories = ['Safety', 'Comfort', 'Entertainment', 'Technology', 'Exterior', 'Interior'];

  if (loading) {
    return (
      <div className="car-features-form">
        <h3>🚗 Car Features</h3>
        <div className="loading-state">Loading features...</div>
      </div>
    );
  }

  return (
    <div className="car-features-form">
      <div className="form-header">
        <h3>🚗 Car Features</h3>
        <span className="feature-count">{features.length} features</span>
      </div>

      <form onSubmit={handleSubmit} className="feature-form">
        <div className="form-row">
          <div className="form-group">
            <label>Category *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              required
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Feature Name *</label>
            <input
              type="text"
              placeholder="e.g., Airbags, Sunroof, ABS"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.available}
                onChange={(e) => setForm({ ...form, available: e.target.checked })}
              />
              Available
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            {editingId ? '✏️ Update Feature' : '➕ Add Feature'}
          </button>
          {editingId && (
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="features-list">
        {features.length === 0 ? (
          <div className="empty-state">
            <p>No features added yet. Add your first feature above!</p>
          </div>
        ) : (
          <div className="features-grid">
            {categories.map(category => {
              const categoryFeatures = features.filter(f => f.category === category);
              if (categoryFeatures.length === 0) return null;
              
              return (
                <div key={category} className="feature-category">
                  <h4>{category}</h4>
                  <ul>
                    {categoryFeatures.map(feature => (
                      <li key={feature.id} className={!feature.available ? 'unavailable' : ''}>
                        <span className="feature-name">
                          {feature.available ? '✅' : '❌'} {feature.name}
                        </span>
                        <div className="feature-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(feature)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(feature.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarFeaturesForm;